/**
 * /well-testing/packages — the package register (GET /well-testing/packages,
 * fieldiq-api#1245, WT-2).
 *
 * A package is a named set of the company's own units (separator, choke
 * manifold, flare stack, test tank…) that goes onto a job together. The units
 * are shared equipment rows; the package and each unit's spec are well
 * testing's. Full width; a row opens the package on its own page. New package
 * is WT_DISPATCH.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Boxes, Package, Plus, Search, Truck } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useWtPackages } from '../../hooks/useWellTesting';
import { queryLoadFailure } from '../../lib/load-failure';
import { canDispatchWt } from '../../lib/well-testing';
import type { WtPackage } from '../../types/well-testing';
import { useOpenRecord } from '../downhole/recordPage';
import { WT_EYEBROW, WtEmpty, WtPanel, WtRegister, type WtColumn } from './WtBits';

export default function WtPackagesScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const [search, setSearch] = useState('');
  const query = useWtPackages();
  const all = query.isLoading || queryLoadFailure(query) ? undefined : query.data?.data;
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = query.data?.data ?? [];
    return q ? list.filter((p) => `${p.name} ${p.description ?? ''}`.toLowerCase().includes(q)) : list;
  }, [query.data, search]);
  const countOf = (pick: (p: WtPackage) => boolean) => displayValue(all ? all.filter(pick).length : undefined);
  const units = all ? all.reduce((n, p) => n + (p.unitCount ?? 0), 0) : undefined;

  const columns: WtColumn<WtPackage>[] = [
    {
      key: 'name',
      header: 'Package',
      render: (p) => (
        <>
          <p className="font-extrabold text-white">{p.name || '—'}</p>
          <p className="mt-0.5 text-xs text-fieldiq-muted">{p.description || '—'}</p>
        </>
      ),
    },
    { key: 'units', header: 'Units', align: 'right', render: (p) => displayValue(p.unitCount ?? undefined) },
    { key: 'job', header: 'On job', render: (p) => <span className="font-mono text-sm">{p.openJobNumber || '—'}</span> },
    { key: 'notes', header: 'Notes', render: (p) => p.notes || '—' },
  ];

  return (
    <div data-testid="wt-packages-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WT_EYEBROW}
        title="Packages"
        description="Sets of your own units that go onto a job together."
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canDispatchWt(user?.role) ? (
            <button type="button" onClick={() => openRecord('/well-testing/packages/new')} className="fi-action fi-action--standard fi-action--primary">
              <Plus className="size-4" aria-hidden="true" />
              New package
            </button>
          ) : undefined
        }
        stats={[
          { icon: <Package className="size-4" aria-hidden="true" />, label: 'Packages', tone: 'neutral', loading: query.isLoading, value: countOf(() => true) },
          { icon: <Boxes className="size-4" aria-hidden="true" />, label: 'Units', tone: 'neutral', loading: query.isLoading, value: displayValue(units) },
          { icon: <Truck className="size-4" aria-hidden="true" />, label: 'On a job', tone: 'accent', loading: query.isLoading, value: countOf((p) => !!p.openJobNumber) },
        ]}
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <label className="relative block">
            <span className="sr-only">Search packages</span>
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search packages"
              className="h-10 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card pl-9 pr-3 text-sm text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
            />
          </label>
          <WtPanel
            eyebrow="Package register"
            title="Packages"
            testId="wt-package-register"
            footer={!query.isLoading && !queryLoadFailure(query) && rows.length > 0 ? `${rows.length} package${rows.length === 1 ? '' : 's'}` : undefined}
          >
            <WtRegister
              query={query}
              rows={rows}
              columns={columns}
              rail={(p) => (p.openJobNumber ? 'border-l-fi-amber' : 'border-l-transparent')}
              onOpen={(p) => openRecord(`/well-testing/packages/${p.id}`)}
              subject="packages"
              testId="wt-package"
              minWidth="min-w-[640px]"
              empty={search.trim() ? <WtEmpty>No package matches this search.</WtEmpty> : <WtEmpty>There are no packages yet.</WtEmpty>}
              card={(p) => (
                <>
                  <p className="truncate text-sm font-extrabold text-white">{p.name || '—'}</p>
                  <p className="mt-0.5 text-xs text-fieldiq-muted">
                    {displayValue(p.unitCount ?? undefined)} units · {p.openJobNumber ? `On ${p.openJobNumber}` : 'Not on a job'}
                  </p>
                </>
              )}
            />
          </WtPanel>
        </div>
      </section>
    </div>
  );
}
